// -----------------------------------------------------------------------------
// ui/timeline/controls.ts — the timeline's control bar.
//
// Split the selected clip at the playhead, delete it, add tracks, and zoom the
// timeline in/out. Each button dispatches a command (or nudges the zoom signal);
// nothing here mutates state directly. Split/Delete enable only when a clip is
// selected. (CLAUDE.md §6 Tier 0: "controls (play/split/zoom)".)
// -----------------------------------------------------------------------------
import { RemoveClipCommand, SplitClipCommand } from '../../core/commands/clip-commands';
import { AddTrackCommand } from '../../core/commands/track-commands';
import { type TrackKind, clipEnd, locateClip } from '../../core/model';
import type { EditorContext } from '../context';
import { el } from '../dom';

const MIN_ZOOM = 20;
const MAX_ZOOM = 400;

export function createTimelineControls(ctx: EditorContext): HTMLElement {
  const { store, player, signals } = ctx;

  const splitBtn = el('button', { class: 'zf-btn zf-btn-ghost', onclick: doSplit }, '✂ Split');
  const deleteBtn = el('button', { class: 'zf-btn zf-btn-ghost', onclick: doDelete }, '🗑 Delete');
  const addVideo = el(
    'button',
    { class: 'zf-btn zf-btn-ghost', onclick: () => addTrack('video') },
    '+ Video track',
  );
  const addText = el(
    'button',
    { class: 'zf-btn zf-btn-ghost', onclick: () => addTrack('text') },
    '+ Text track',
  );

  const zoomLabel = el('span', {}, '');
  const zoomOut = el('button', { class: 'zf-btn zf-btn-ghost', onclick: () => zoom(0.8) }, '−');
  const zoomIn = el('button', { class: 'zf-btn zf-btn-ghost', onclick: () => zoom(1.25) }, '+');

  const bar = el(
    'div',
    { class: 'zf-timeline-controls' },
    splitBtn,
    deleteBtn,
    addVideo,
    addText,
    el('span', { class: 'zf-spacer' }),
    el('span', { class: 'zf-zoom' }, 'Zoom', zoomOut, zoomLabel, zoomIn),
  );

  function refresh(): void {
    const hasSelection = store.state.selectedClipId !== null;
    splitBtn.toggleAttribute('disabled', !hasSelection);
    deleteBtn.toggleAttribute('disabled', !hasSelection);
    zoomLabel.textContent = `${Math.round(signals.pxPerSecond.get())}px/s`;
  }

  store.subscribe(refresh);
  signals.pxPerSecond.subscribe(refresh);
  refresh();

  function doSplit(): void {
    const id = store.state.selectedClipId;
    if (!id) return;
    const loc = locateClip(store.state.project, id);
    if (!loc) return;
    const t = player.getTime();
    if (t <= loc.clip.startSec || t >= clipEnd(loc.clip)) {
      ctx.setStatus('Move the playhead inside the selected clip, then split.', 'error');
      return;
    }
    store.dispatch(new SplitClipCommand(id, t));
    ctx.setStatus('Clip split.');
  }

  function doDelete(): void {
    const id = store.state.selectedClipId;
    if (!id) return;
    store.dispatch(new RemoveClipCommand(id));
    store.select(null);
    ctx.setStatus('Clip deleted.');
  }

  function addTrack(kind: TrackKind): void {
    const count = store.state.project.tracks.filter((t) => t.kind === kind).length + 1;
    const name = `${kind[0].toUpperCase()}${kind.slice(1)} ${count}`;
    store.dispatch(new AddTrackCommand(kind, name));
  }

  function zoom(factor: number): void {
    signals.pxPerSecond.update((v) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, v * factor)));
  }

  return bar;
}
